/** Soft, muted palette for analytics charts and scope chips. */

export const CHART_SERIES_COLORS = [
  "#7BA7D7", // powder blue
  "#7EC8B8", // seafoam
  "#B8A9D9", // lavender
  "#E8A598", // dusty coral
  "#98C1A8", // sage
  "#A8C5E8", // sky
  "#D4A5C9", // mauve
  "#C9B896", // sand
] as const;

export const CHART_COMPARE_COLOR = "#94A3B8"; // soft slate

export const CHART_PRIMARY = CHART_SERIES_COLORS[0];

export interface SoftChipColor {
  bg: string;
  text: string;
  border: string;
}

export const CHIP_COLORS: SoftChipColor[] = [
  { bg: "#EDF3FA", text: "#5B7FA6", border: "#D8E6F5" },
  { bg: "#EAF6F2", text: "#4F8A7A", border: "#D0EBE3" },
  { bg: "#F2EDFA", text: "#7A6B9E", border: "#E3DAF2" },
  { bg: "#FAF0ED", text: "#A67162", border: "#F2DDD6" },
  { bg: "#EDF5F0", text: "#5A8A72", border: "#D8EBE0" },
  { bg: "#EDF2FA", text: "#627FA8", border: "#D8E3F2" },
  { bg: "#FAEDF5", text: "#946B88", border: "#EFDAE8" },
  { bg: "#F5F2EB", text: "#8A7D62", border: "#EAE4D8" },
];

export function chipColorForIndex(index: number): SoftChipColor {
  return CHIP_COLORS[index % CHIP_COLORS.length]!;
}

export function seriesColorForIndex(index: number): string {
  return CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]!;
}

/** Canonical series/chip key for a rep — must match `resolveEntitySeries`. */
export function entityColorKey(kind: "team" | "rep", id: number): string {
  return `${kind}-${id}`;
}

/** Stable series/chip key for a combined team group (syncore Primary Rep ref). */
export function teamGroupColorKey(groupKey: string): string {
  return `team-gk:${groupKey}`;
}

/**
 * Assign palette slots in sorted key order so each entity in the set gets a
 * distinct color when count <= palette length (avoids warehouse id hash collisions).
 */
export function buildStableColorIndexMap(keys: Iterable<string>): Map<string, number> {
  const sorted = [...new Set(keys)].sort((a, b) => a.localeCompare(b));
  return new Map(sorted.map((key, index) => [key, index]));
}

/** Stable palette slot for a team, rep, or series key (hash fallback). */
export function colorIndexForEntityId(id: string | number): number {
  const text = String(id);
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % CHART_SERIES_COLORS.length;
}

export function colorIndexForKey(key: string, stableMap?: ReadonlyMap<string, number>): number {
  const stable = stableMap?.get(key);
  if (stable != null) {
    return stable % CHART_SERIES_COLORS.length;
  }
  return colorIndexForEntityId(key);
}

export function seriesColorForKey(key: string, stableMap?: ReadonlyMap<string, number>): string {
  return seriesColorForIndex(colorIndexForKey(key, stableMap));
}

export function chipColorForKey(
  key: string,
  stableMap?: ReadonlyMap<string, number>,
): SoftChipColor {
  return chipColorForIndex(colorIndexForKey(key, stableMap));
}

export function seriesColorForEntityId(id: string | number): string {
  return seriesColorForKey(String(id));
}

export function chipColorForEntityId(id: string | number): SoftChipColor {
  return chipColorForKey(String(id));
}

/** Build a smooth SVG path (Catmull-Rom style cubic) through points. */
export function buildSmoothLinePath(coords: Array<{ x: number; y: number }>): string {
  if (coords.length === 0) return "";
  if (coords.length === 1) return `M ${coords[0]!.x} ${coords[0]!.y}`;
  if (coords.length === 2) {
    return `M ${coords[0]!.x} ${coords[0]!.y} L ${coords[1]!.x} ${coords[1]!.y}`;
  }

  let path = `M ${coords[0]!.x} ${coords[0]!.y}`;

  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[i - 1] ?? coords[i]!;
    const p1 = coords[i]!;
    const p2 = coords[i + 1]!;
    const p3 = coords[i + 2] ?? p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }

  return path;
}

export function buildSmoothAreaPath(
  coords: Array<{ x: number; y: number }>,
  baseY: number,
): string {
  if (coords.length === 0) return "";
  const line = buildSmoothLinePath(coords);
  const last = coords.at(-1)!;
  const first = coords[0]!;
  return `${line} L ${last.x} ${baseY} L ${first.x} ${baseY} Z`;
}
